import Shepherd from 'shepherd.js';
import { tours } from './registry.js';
import { ALWAYS_FIRST_TIME } from './config.js';

const STORAGE_KEY = 'mgs_tours_seen_v1';

function loadSeen() {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        return raw ? JSON.parse(raw) : {};
    } catch (e) {
        return {};
    }
}

function saveSeen(map) {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(map));
    } catch (e) { /* ignore */ }
}

export function isTourSeen(tourId) {
    if (ALWAYS_FIRST_TIME) return false;
    return Boolean(loadSeen()[tourId]);
}

export function markTourSeen(tourId) {
    const seen = loadSeen();
    seen[tourId] = new Date().toISOString();
    saveSeen(seen);
}

export function resetTourSeen(tourId) {
    const seen = loadSeen();
    if (tourId) {
        delete seen[tourId];
    } else {
        Object.keys(seen).forEach(k => delete seen[k]);
    }
    saveSeen(seen);
}

let activeTour = null;
let lastStart = { id: null, at: 0 };

// Wraps a step config so the tour cancels itself when the modal/panel that
// hosts the step's UI disappears (user closed the wizard, hit ESC, etc.).
// A step opts in by setting `cancelIfMissing: '<selector>'`. The guard is
// only watched starting 500ms after `show` to avoid false triggers during
// transitions between modals.
function wrapStepWithCancelGuard(stepConfig) {
    if (!stepConfig.cancelIfMissing) return stepConfig;

    const guard = stepConfig.cancelIfMissing;
    const cleaned = { ...stepConfig };
    delete cleaned.cancelIfMissing;

    const origWhen = stepConfig.when || {};
    let observer = null;
    let timer = null;

    const teardown = () => {
        if (timer) { clearTimeout(timer); timer = null; }
        if (observer) { observer.disconnect(); observer = null; }
    };

    cleaned.when = {
        ...origWhen,
        show() {
            if (origWhen.show) origWhen.show.call(this);
            teardown();
            const tour = this.tour;
            const stepInstance = this;
            // Helper: only act if this step is still the active one. Mutation
            // observer callbacks can fire after the tour has advanced (e.g. the
            // modal close that happens as part of advancing IS itself a mutation).
            const stillActive = () => {
                try {
                    return tour.getCurrentStep && tour.getCurrentStep() === stepInstance;
                } catch (e) {
                    return false;
                }
            };
            timer = setTimeout(() => {
                timer = null;
                if (!stillActive()) return;
                if (!document.querySelector(guard)) {
                    try { tour.cancel(); } catch (e) { /* ignore */ }
                    return;
                }
                observer = new MutationObserver(() => {
                    if (!stillActive()) {
                        teardown();
                        return;
                    }
                    if (!document.querySelector(guard)) {
                        teardown();
                        try { tour.cancel(); } catch (e) { /* ignore */ }
                    }
                });
                observer.observe(document.body, { childList: true, subtree: true });
            }, 500);
        },
        hide() {
            if (origWhen.hide) origWhen.hide.call(this);
            teardown();
        },
    };

    return cleaned;
}

export function startTour(tourId) {
    const def = tours.find(t => t.id === tourId);
    if (!def) {
        console.warn('[tours] Unknown tour:', tourId);
        return null;
    }

    // Debounce: ignore identical start requests within 800ms (handles the case
    // where the help page navigates to a tour's home page, which also triggers
    // the page's auto-launch path for the same tour).
    const now = Date.now();
    if (lastStart.id === tourId && (now - lastStart.at) < 800) {
        return activeTour;
    }
    lastStart = { id: tourId, at: now };

    if (activeTour) {
        try { activeTour.cancel(); } catch (e) { /* ignore */ }
        activeTour = null;
    }

    const tour = new Shepherd.Tour({
        // useModalOverlay must stay false: the overlay dims the page AND blocks
        // pointer events outside the spotlighted element, which prevents the
        // user from clicking the Continue/Cancel buttons that sit outside the
        // attached element (e.g. modal footers). The tooltip's arrow + the
        // AmlModal's own backdrop provide enough visual focus.
        useModalOverlay: false,
        defaultStepOptions: {
            cancelIcon: { enabled: true },
            classes: 'mgs-tour-step',
            scrollTo: { behavior: 'smooth', block: 'center' },
        },
    });

    const steps = typeof def.steps === 'function' ? def.steps() : def.steps;
    steps.forEach(step => tour.addStep(wrapStepWithCancelGuard(step)));

    tour.on('complete', () => {
        markTourSeen(tourId);
        activeTour = null;
    });
    tour.on('cancel', () => {
        activeTour = null;
    });

    activeTour = tour;
    tour.start();
    return tour;
}

export function getActiveTour() {
    return activeTour;
}

/**
 * Cancel the active tour ONLY if its current step has nowhere to anchor.
 * Used by Clients.vue when transitioning between list and detail views:
 *
 *   - Modal-style explainer tours (CDD status, risk ratings — no attachTo
 *     at all) → cancel immediately. Those steps weren't pointing at anything
 *     in the first place, so navigating away is the user's signal to bail.
 *   - Action-driven tours (editClient, associatedPersons, etc.) advance to
 *     a step whose target lives on the new view. We give that target a
 *     short grace window to appear (the API call + Vue render) before
 *     deciding to cancel — otherwise we kill the tour during the gap
 *     between Shepherd advancing and the new tooltip rendering.
 *
 * Caller doesn't need to delay this — the helper handles its own timing.
 */
export function cancelIfNoAnchor() {
    if (!activeTour) return false;

    const stepHasAnchor = () => {
        if (!activeTour) return false;
        let s = null;
        try { s = activeTour.getCurrentStep && activeTour.getCurrentStep(); }
        catch (e) { return false; }
        if (!s) return false;
        const at = s.options && s.options.attachTo;
        const sel = at && at.element;
        if (!sel) return null;  // modal-style step — distinct from "no anchor in DOM"
        return !!document.querySelector(sel);
    };

    const initial = stepHasAnchor();
    if (initial === true) {
        return false;  // already anchored to something visible
    }
    if (initial === null) {
        // Modal-style step (no attachTo). Nothing to wait for — cancel.
        return cancelActiveTour();
    }

    // Step expects an anchor but it isn't in the DOM yet. Poll briefly
    // (Shepherd may have just advanced; the destination view may still
    // be rendering). If the anchor shows up within the grace window we
    // leave the tour alone; otherwise we cancel.
    const graceMs = 800;
    const startedAt = Date.now();
    const poll = () => {
        if (!activeTour) return;
        const state = stepHasAnchor();
        if (state === true) return;  // appeared — tour stays
        if (state === null) {        // step changed to modal-style mid-poll
            cancelActiveTour();
            return;
        }
        if (Date.now() - startedAt >= graceMs) {
            cancelActiveTour();
            return;
        }
        setTimeout(poll, 50);
    };
    setTimeout(poll, 50);
    return false;
}

/**
 * Cancel the currently-active tour, if any. Called by Aml.vue's setPage()
 * when the user navigates between sidebar pages — without this, the
 * tooltip stays visible (usually drifting to the top-left corner of the
 * viewport) because its target element has just been unmounted.
 *
 * Belt-and-suspenders: we call Shepherd's own cancel() (the proper path,
 * fires events + updates state) AND then forcibly remove any leftover
 * .shepherd-element / .shepherd-modal-overlay-container nodes from the
 * DOM. The hard cleanup catches the case where Shepherd's cancel runs
 * during Vue's page teardown and the orphaned tooltip survives.
 *
 * Returns true if a tour was active and got cancelled, false otherwise.
 */
export function cancelActiveTour() {
    const hadActive = !!activeTour;
    if (activeTour) {
        try {
            activeTour.cancel();
        } catch (e) {
            /* ignore — Shepherd will null out internals on next start */
        }
        activeTour = null;
    }
    // Hard cleanup. Any orphaned tooltip / overlay nodes get yanked from
    // the DOM regardless of whether Shepherd's cancel made it through. We
    // also clear shepherd-active classes that style the body during a
    // tour, in case those got left behind.
    try {
        document.querySelectorAll(
            '.shepherd-element, .shepherd-modal-overlay-container'
        ).forEach(el => el.remove());
        document.body.classList.remove('shepherd-active');
        document.documentElement.classList.remove('shepherd-active');
    } catch (e) { /* SSR / no-DOM safety */ }
    return hadActive;
}
